import { useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Sparkles, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const FEATURE_LABELS = {
  kds: "Cozinha (KDS)",
  tasks: "Tarefas",
  waitlist: "Fila de espera",
  webhook: "Webhooks",
  nfce: "NFC-e",
  marketing: "Marketing & Reativação",
  cashback: "Cashback",
  reports: "Relatórios avançados",
  attendance: "Registro de ponto",
  delivery: "Delivery",
  intelligence: "Intelligence",
  recipes: "Fichas técnicas",
  dre: "DRE",
  replenishment: "Reposição",
  metas: "Metas",
  crm: "CRM",
  multi_branch: "Várias filiais",
  roastery: "Torrefação",
  nfe: "NF-e",
  purchases: "Compras",
};

const PLAN_FEATURES_DEFAULTS = {
  starter:  ["tasks", "waitlist", "cashback", "reports"],
  pro:      ["tasks", "waitlist", "cashback", "reports", "kds", "webhook", "marketing"],
  business: ["tasks", "waitlist", "cashback", "reports", "kds", "webhook", "marketing", "nfce"],
};

function planIncludes(plan, feature) {
  return (PLAN_FEATURES_DEFAULTS[plan] || []).includes(feature);
}

/**
 * Global listener for "feature-blocked" events triggered by the axios interceptor.
 * Shows a modal with plan comparison + upgrade CTA.
 */
export default function UpgradePrompt() {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState(null);
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState("pro");
  const [loading, setLoading] = useState(false);
  const [plansLoaded, setPlansLoaded] = useState(false);

  useEffect(() => {
    const handler = (ev) => {
      if (window.location.pathname.startsWith("/admin")) return; // don't prompt platform admin
      setFeature(ev.detail?.feature);
      setOpen(true);
      setPlansLoaded(false);
      // lazy-load plan list for pricing display
      api.get("/subscription/plans").then(r => setPlans(r.data || [])).catch(()=>{})
        .finally(() => setPlansLoaded(true));
      api.get("/subscription").then(r => setCurrentPlan(r.data?.plan || "pro")).catch(()=>{});
    };
    window.addEventListener("feature-blocked", handler);
    return () => window.removeEventListener("feature-blocked", handler);
  }, []);

  const goUpgrade = async (targetPlan, cycle) => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan: targetPlan,
        cycle,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Não foi possível iniciar o checkout. Tente novamente.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no checkout");
    } finally { setLoading(false); }
  };

  const label = FEATURE_LABELS[feature] || feature;
  // If the feature is already included in the current plan by default, the block is an admin override
  // → upgrading wouldn't help; show "contact admin" instead.
  const blockedByAdmin = planIncludes(currentPlan, feature);
  // normalize key field (/subscription/plans returns `id`, /admin/plans returns `key`)
  const plansNorm = plans.map(p => ({ ...p, key: p.key || p.id }));
  const currentIdx = ["starter","pro","business"].indexOf(currentPlan);
  const suggestedPlan = plansNorm.find((p) => {
    const pIdx = ["starter","pro","business"].indexOf(p.key);
    return pIdx > currentIdx && planIncludes(p.key, feature);
  }) || plansNorm.find(p => planIncludes(p.key, feature)) || null;
  const isLowerOrSame = blockedByAdmin || !suggestedPlan || (["starter","pro","business"].indexOf(suggestedPlan.key) <= currentIdx);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="upgrade-prompt-dialog">
        <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 text-white px-6 py-8 text-center relative">
          <button onClick={()=>setOpen(false)} className="absolute top-3 right-3 text-white/70 hover:text-white" data-testid="upgrade-close-btn">
            <X className="w-5 h-5" />
          </button>
          <div className="inline-flex w-14 h-14 rounded-full bg-white/20 items-center justify-center mb-3">
            <Lock className="w-7 h-7" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-extrabold tracking-tight">Recurso bloqueado</DialogTitle>
          </DialogHeader>
          <p className="text-white/90 text-sm mt-2">
            <b>{label}</b> não está liberado para a sua conta.
          </p>
        </div>

        <div className="p-6 space-y-4">
          {!suggestedPlan && !plansLoaded ? (
            <div className="text-sm text-muted-foreground text-center">Carregando opções de plano...</div>
          ) : isLowerOrSame ? (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Este recurso pode estar desativado para a sua conta. Entre em contato com o administrador da plataforma OPLYX para reativar.
              </p>
              <Button className="w-full rounded-full" variant="outline" onClick={()=>setOpen(false)} data-testid="upgrade-dismiss-btn">
                Entendi
              </Button>
            </>
          ) : (
            <>
              <div className="rounded-xl border p-4 bg-emerald-50 border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-700" />
                    <span className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Plano sugerido</span>
                  </div>
                  <Badge className="bg-emerald-600 uppercase text-[10px]">{suggestedPlan.name}</Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold text-neutral-900">{brl(suggestedPlan.month)}</span>
                  <span className="text-xs text-muted-foreground">/mês</span>
                  <span className="text-xs text-muted-foreground ml-2">ou {brl(suggestedPlan.year)}/ano</span>
                </div>
                <div className="mt-3 space-y-1">
                  {(PLAN_FEATURES_DEFAULTS[suggestedPlan.key] || []).map(f => (
                    <div key={f} className={`flex items-center gap-2 text-sm ${f === feature ? "font-semibold text-emerald-800" : "text-neutral-700"}`}>
                      <CheckCircle2 className={`w-4 h-4 ${f === feature ? "text-emerald-700" : "text-neutral-400"}`} />
                      {FEATURE_LABELS[f] || f}
                      {f === feature && <Badge className="bg-amber-500 text-[10px] ml-auto">Você precisa disso</Badge>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button disabled={loading} onClick={()=>goUpgrade(suggestedPlan.key, "month")} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="upgrade-monthly-btn">
                  Assinar mensal
                </Button>
                <Button disabled={loading} onClick={()=>goUpgrade(suggestedPlan.key, "year")} className="rounded-full bg-neutral-900 hover:bg-neutral-800" data-testid="upgrade-yearly-btn">
                  Assinar anual (-{suggestedPlan.year && suggestedPlan.month ? Math.round((1 - (suggestedPlan.year / (suggestedPlan.month * 12))) * 100) : 0}%)
                </Button>
              </div>
              <button onClick={()=>setOpen(false)} className="w-full text-xs text-muted-foreground hover:underline text-center" data-testid="upgrade-later-btn">
                Lembrar depois
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
